import { ensureAccountAction } from "@/actions/account";
import type { Market } from "@features/market/config/i18n-config";
import { FirebaseError } from "firebase/app";
import type {
  Auth,
  FacebookAuthProvider,
  GoogleAuthProvider,
  Persistence,
} from "firebase/auth";
import clearAuthCookie from "./clearAuthCookie";
import { clearSessionEstablishment } from "./establishSession";

export type ProviderSignInProps = {
  auth: Auth;
  type?: Persistence;
  market: Market;
  provider: GoogleAuthProvider | FacebookAuthProvider;
};

export default async function signInWithProvider({
  auth,
  type,
  market,
  provider,
}: ProviderSignInProps) {
  const {
    browserLocalPersistence,
    browserPopupRedirectResolver,
    getAdditionalUserInfo,
    setPersistence,
    signInWithPopup,
  } = await import("firebase/auth");

  await setPersistence(auth, type ?? browserLocalPersistence);

  try {
    const response = await signInWithPopup(
      auth,
      provider,
      browserPopupRedirectResolver,
    );

    if (!response.user) {
      throw new Error("No user in response");
    }

    const info = getAdditionalUserInfo(response);

    const ensuranceResult = await ensureAccountAction({
      locale: market.locale,
      ensurance: {
        firebaseAccountId: response.user.uid,
        firstName: info?.profile?.given_name as string,
        lastName: info?.profile?.family_name as string,
        birthDate: "",
        countryCode: market.countryCode,
      },
    });

    if (!ensuranceResult.success) {
      throw new Error("Failed to ensure account");
    }

    return response.user;
  } catch (error) {
    clearSessionEstablishment();
    void clearAuthCookie();
    if (error instanceof FirebaseError || error instanceof Error) {
      throw error;
    }
    throw new Error("An error occurred while signing in with provider");
  }
}
